import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import QuickViewSearch from 'components/QuickViewSearch';
import { useTranslation } from 'react-i18next';
import type {
    FindStoreResult,
    FindStoreResultViewModel,
    ProductInfoModel,
    ProductStoreStockViewModel
} from 'autogen/swagger/all';
import StoreList from './components/storeList';
import ClickAndCollectStoresToggle from './components/ClickAndCollectStoresToggle';
import useFeatureToggles from 'hooks/user/useFeatureToggles';
import {
    ShowSearchButton,
    ChooseStoreButtonText,
    Headline,
    SearchFieldPlaceholder,
    SearchFieldLabel,
    GeoLocationErrorText,
    SummaryCaption,
    SubmitButtonText,
    UseGeoLocationButton,
    ClickAndCollectHeadline
} from 'autogen/translation-keys/trans-website-productstoresearch';
import LoadingContainer from 'components/checkout/components/BasketOverview/components/LoadingContainer';
import styled from 'styled-components';

interface IQuickViewContent {
    findStoreResult?: FindStoreResult;
    numberOfStores?: FindStoreResultViewModel['numberOfStores'];
    initialPreferredStore?: ProductStoreStockViewModel['initialPreferredStore'];
    showSelectParcelShop?: boolean;
    productInfo: ProductInfoModel;
    showClickAndCollectToggle: boolean;
    clickAndCollectLabel?: string;
    clickAndCollectTimeTag?: string;
    showClickAndCollectOnly: boolean;
    isClickAndCollect?: boolean;
    setShowClickAndCollectOnly: Dispatch<SetStateAction<boolean>>;
    incrementPage?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => Promise<any>;
    isBasket?: boolean;
    isLoading?: boolean;
}

const StyledLoadingContainer = styled(LoadingContainer)`
    display: contents;
`;

export default function StoreListQuickView({
    findStoreResult,
    numberOfStores,
    initialPreferredStore,
    showSelectParcelShop,
    productInfo,
    showClickAndCollectToggle,
    clickAndCollectLabel,
    clickAndCollectTimeTag,
    showClickAndCollectOnly,
    isClickAndCollect,
    setShowClickAndCollectOnly,
    incrementPage,
    isBasket,
    isLoading
}: IQuickViewContent) {
    const searchQuery = findStoreResult?.searchData.addressQuery || undefined;
    const { t } = useTranslation();
    const { clickAndCollectEnabled } = useFeatureToggles();

    const translations = {
        headline: isClickAndCollect ? t(ClickAndCollectHeadline) : t(Headline),
        search: {
            placeholder: t(SearchFieldPlaceholder),
            searchResultText: t(SummaryCaption),
            submitButtonText: t(SubmitButtonText),
            textFieldTitle: t(SearchFieldLabel),
            showSearch: t(ShowSearchButton),
            geoLocation: {
                button: t(UseGeoLocationButton),
                errorText: t(GeoLocationErrorText)
            }
        }
    };

    return (
        <QuickViewSearch
            translations={translations}
            inputType="text"
            searchQuery={searchQuery}
            trackingCategory="storeStockCheckerQuickView"
            productInfo={productInfo}
        >
            <StyledLoadingContainer isLoading={!!isLoading}>
                {clickAndCollectEnabled && showClickAndCollectToggle && (
                    <ClickAndCollectStoresToggle
                        clickAndCollectTimeTag={clickAndCollectTimeTag}
                        clickAndCollectLabel={clickAndCollectLabel}
                        showClickAndCollectOnly={showClickAndCollectOnly}
                        setShowClickAndCollectOnly={setShowClickAndCollectOnly}
                        showBullet={false}
                    />
                )}
                {findStoreResult && (
                    <StoreList
                        isLoading={!!isLoading}
                        {...findStoreResult}
                        numberOfStores={numberOfStores}
                        translations={{ chooseStoreButtonText: t(ChooseStoreButtonText) }}
                        initialPreferredStore={initialPreferredStore}
                        showSelectParcelShop={showSelectParcelShop}
                        incrementPage={incrementPage}
                        productInfo={productInfo}
                        clickAndCollectTimeTag={clickAndCollectTimeTag}
                        isBasket={isBasket}
                    />
                )}
            </StyledLoadingContainer>
        </QuickViewSearch>
    );
}
